import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

export type Feature = 
  | 'recurring_invoices'
  | 'invoice_sharing'
  | 'ai_cfo'
  | 'tax_vault'
  | 'contracts'
  | 'pdf_export';

const FEATURE_PLANS: Record<Feature, ('free' | 'pro' | 'business')[]> = {
  recurring_invoices: ['pro', 'business'],
  invoice_sharing: ['pro', 'business'],
  ai_cfo: ['pro', 'business'],
  pdf_export: ['pro', 'business'],
  tax_vault: ['pro', 'business'],  // Pro gets Tax Vault per pricing page
  contracts: ['business'],
};

const FEATURE_NAMES: Record<Feature, string> = {
  recurring_invoices: 'Recurring Invoices',
  invoice_sharing: 'Invoice Sharing',
  ai_cfo: 'SOLO AI Assistant',
  pdf_export: 'PDF Export',
  tax_vault: 'Tax Vault',
  contracts: 'Contracts',
};

const PLAN_DISPLAY: Record<string, string> = {
  free: 'Free',
  pro: 'Pro',
  business: 'Business',
};

export function usePlanGating() {
  const { profile } = useAuth();
  const currentPlan = profile?.plan || 'free';

  const canAccess = (feature: Feature): boolean => {
    const allowedPlans = FEATURE_PLANS[feature];
    return allowedPlans.includes(currentPlan);
  };

  const requireFeature = (feature: Feature): boolean => {
    if (canAccess(feature)) return true;
    
    const requiredPlan = FEATURE_PLANS[feature][0];
    toast.error(`Upgrade to ${PLAN_DISPLAY[requiredPlan]} to access ${FEATURE_NAMES[feature]}`);
    return false;
  };

  const getMinPlanForFeature = (feature: Feature): string => {
    return FEATURE_PLANS[feature][0];
  };

  const isFeatureLocked = (feature: Feature): boolean => {
    return !canAccess(feature);
  };

  return {
    currentPlan,
    canAccess,
    requireFeature,
    getMinPlanForFeature,
    isFeatureLocked,
  };
}
